import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolSite,
  ProtocolWarning,
} from "../types";

/**
 * Fostimon — urofollitropin, the purified urinary FSH that still comes as a
 * powder to dissolve, one vial per 75 or 150 units, and a dose that may take
 * six of them in one syringe.
 *
 * ### Two leaflets, two kits, one procedure
 *
 * The French Fostimonkit notice (ANSM, the presentation withdrawn from the
 * French market in May 2025 but still the only French label) ships the
 * solvent in a prefilled syringe with two needles. The Irish Fostimon leaflet
 * (HPRA, in English) ships the solvent in a glass ampoule and supplies neither
 * syringe nor needles — and says, in so many words, that presentations other
 * than ampoules should be considered for self-administration. The hands are
 * the same once the solvent is in a syringe: through the middle of the
 * stopper, roll, never shake, no foam; draw up with the vial upside down;
 * chain into the next vial for a bigger dose, half a vial for a half step;
 * swap to the fine needle, tap the bubbles up, push to a bead; the thigh or
 * the lower abdomen below the navel, a pinch, a dart at 45° or 90°, slow and
 * steady; out quickly, press, a gentle massage. The walk says both solvents in
 * one sentence and the rest is one procedure, step for step in both labels.
 *
 * ### What the label leaves to the doctor
 *
 * No needle gauge, no site rotation, no "let it dry" on the skin — the leaflet
 * says the doctor or nurse has already shown her where. The walk says nothing
 * the leaflet does not, and the fiche lists the conventions it chose not to
 * add. The Irish leaflet also allows the intramuscular route; the French one
 * is subcutaneous only, and the self-injection instructions in both are
 * subcutaneous, so that is the only route here.
 *
 * ### The dose
 *
 * Units, any multiple of half a vial up to the label's daily maximum, titrated
 * by the clinic. No strength list: a record of 112.5 IU is as real as one of
 * 150 IU, and the guard only has to agree on the unit.
 *
 * Restated from `content/data/medications/gonadotropins.json` (the
 * urofollitropin entry, retitled from Bravelle, the US brand recalled in 2015),
 * rewritten against these two leaflets — it carried gauges, a two-hour
 * missed-dose rule, an intramuscular buttock and a ten-second hold that
 * neither label has.
 */

const FOSTIMON_BRANDS = [
  { name: "Fostimon", markets: ["IE", "IT", "NL", "EU"] },
] as const satisfies readonly ProtocolBrand[];

/** The leaflet's own precondition for self-injection, on the first card. */
const FIRST_INJECTION: ProtocolWarning = {
  id: "fostimon.first-injection",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "First injection - Inject yourself only once your doctor has let you practise a subcutaneous injection, shown you where you can inject, shown you how to prepare the solution and explained how to prepare the right dose.",
    fr: "Première injection - Ne vous injectez vous-même qu'une fois que votre médecin vous a fait pratiquer une injection sous-cutanée, vous a indiqué les zones d'injection possibles, vous a montré comment préparer la solution et vous a indiqué précisément comment préparer la bonne dose.",
  },
  sourceSegment:
    "⚠ First injection - Inject yourself only once your doctor has let you practise a subcutaneous injection, shown you where you can inject, shown you how to prepare the solution and explained how to prepare the right dose.",
};

/** Both leaflets, the same two sentences, no hour. */
const MISSED_DOSE: ProtocolWarning = {
  id: "fostimon.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - Take it at the next normal time for an injection. Do not take a double dose to make up for a forgotten dose.",
    fr: "Dose oubliée - Prenez-la comme prévu au moment de l'injection suivante. Ne prenez pas de dose double pour compenser la dose simple que vous avez oublié de prendre.",
  },
  sourceSegment:
    "⚠ Missed dose - Take it at the next normal time for an injection. Do not take a double dose to make up for a forgotten dose.",
};

/**
 * The leaflet's "stop and see your doctor immediately" is for ovarian
 * hyperstimulation, and the first signs it names are these four. Breathing is
 * not among them and is not said, so this routes to the clinic, not to
 * emergency services.
 */
const OHSS: ProtocolWarning = {
  id: "fostimon.ohss",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Hyperstimulation - Stop the injections and see your doctor immediately if you have pain in the lower abdomen, nausea, vomiting or weight gain: these are the first signs of ovarian hyperstimulation.",
    fr: "Hyperstimulation - Arrêtez les injections et consultez votre médecin immédiatement en cas de douleur dans la région abdominale basse, de nausées, de vomissements ou de prise de poids : ce sont les premiers signes d'une hyperstimulation ovarienne.",
  },
  sourceSegment:
    "⚠ Hyperstimulation - Stop the injections and see your doctor immediately if you have pain in the lower abdomen, nausea, vomiting or weight gain: these are the first signs of ovarian hyperstimulation.",
};

/**
 * Both leaflets: "the usual places are the thigh or the lower abdominal wall
 * below the navel", and the doctor has already said which. So she is asked.
 */
const SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Clean the site - Inject where your doctor or nurse showed you: here, on your abdomen below the navel. Wipe the injection site with an alcohol swab.",
      fr: "Désinfecter le site - Injectez là où votre médecin ou votre infirmière vous l'a indiqué : ici, sur le ventre en dessous du nombril. Désinfectez la zone où l'injection va être faite avec un coton imbibé d'alcool.",
    },
  },
  {
    id: "thigh",
    label: { en: "Thigh", fr: "Cuisse" },
    copy: {
      en: "Clean the site - Inject where your doctor or nurse showed you: here, on your thigh. Wipe the injection site with an alcohol swab.",
      fr: "Désinfecter le site - Injectez là où votre médecin ou votre infirmière vous l'a indiqué : ici, sur la cuisse. Désinfectez la zone où l'injection va être faite avec un coton imbibé d'alcool.",
    },
  },
];

export const FOSTIMON_PROTOCOL: InjectionProtocol = {
  medicationSlug: "fostimon",
  brands: FOSTIMON_BRANDS,
  aliases: ["Urofollitropin", "Urofollitropine", "Fostimonkit"],
  indication: "stimulation",
  dose: { unit: "IU" },
  form: "vial",
  prescriptionForms: ["lyophilized_powder"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/gonadotropins.json#urofollitropin",
  sites: SITES,
  steps: [
    {
      // "A syringe if one is not supplied": the Irish box has none, the
      // French box has the solvent in one. The list is the leaflets' own.
      id: "fostimon.vial.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Set up a clean work surface and wash your hands; it is important that your hands and the items you use are as clean as possible. Set out two alcohol swabs, one vial of {brand} powder, the solvent that came with it, a syringe if one is not supplied, a needle for preparing the injection, a fine needle for injection under the skin, and a sharps container. Use only the solvent provided in the package, and prepare the solution just before injecting. One injection a day, under the skin, at the dose your doctor set.",
        fr: "Préparer - Installez une surface de travail propre et lavez-vous les mains ; il est important que vos mains et les accessoires que vous utilisez soient aussi propres que possible. Disposez deux cotons imbibés d'alcool, un flacon de poudre de {brand}, le solvant fourni avec, une seringue si elle n'est pas fournie, une aiguille pour la reconstitution, une aiguille fine pour l'injection sous-cutanée et un collecteur. N'utilisez que le solvant fourni dans l'emballage, et préparez la solution juste avant l'injection. Une injection par jour, sous la peau, à la dose fixée par votre médecin.",
      },
      warnings: [FIRST_INJECTION, MISSED_DOSE],
    },
    {
      // The one sentence that covers both kits is the first; the rest is the
      // leaflets' steps 1 to 3, in their order.
      id: "fostimon.vial.reconstitute",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Reconstitute - Fit the long preparation needle onto the syringe of solvent, or draw the solvent up from its ampoule, and lay the syringe down without touching the needle. Lift off the coloured plastic cap of the powder vial, wipe the rubber stopper with an alcohol swab and let it dry. Push the needle through the middle of the stopper and inject the solvent gently onto the powder, pressing the plunger down firmly so that all of it goes in. Do not shake: gently roll the vial between your hands until the powder has completely dissolved, taking care not to create foam.",
        fr: "Reconstituer - Montez l'aiguille longue pour reconstitution sur la seringue de solvant, ou prélevez le solvant dans son ampoule, et posez la seringue sans toucher l'aiguille. Retirez le capuchon plastique coloré du flacon de poudre, désinfectez le bouchon en caoutchouc avec un coton imbibé d'alcool et laissez sécher. Enfoncez l'aiguille au milieu du bouchon et injectez doucement le solvant sur la poudre, en appuyant fermement sur le piston pour faire descendre toute la solution. Ne secouez pas : faites doucement rouler le flacon entre vos mains jusqu'à dissolution complète de la poudre, en évitant la formation de mousse.",
      },
    },
    {
      // Step 4 of the leaflets, then their "higher doses" paragraph: the
      // chain of vials and the half vial. `{dose}` says her own number here,
      // where the number decides how many vials go into the syringe.
      id: "fostimon.vial.draw-up-the-dose",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Draw up the dose - With the needle still in the vial, turn the vial upside down, keep the tip of the needle under the liquid and pull the plunger gently to draw all the solution into the syringe. If your prescribed dose{dose} needs more than one vial, inject this solution slowly into the next powder vial, roll it until dissolved and draw it up again, until every vial of your dose is in the syringe; for a half-vial dose, draw up only half of the solution. Check that the solution is clear and colourless.",
        fr: "Prélever la dose - L'aiguille toujours dans le flacon, retournez le flacon tête en bas, gardez l'extrémité de l'aiguille sous le liquide et tirez doucement sur le piston pour aspirer toute la solution dans la seringue. Si votre dose prescrite{dose} nécessite plus d'un flacon, injectez lentement cette solution dans le flacon de poudre suivant, roulez-le jusqu'à dissolution et aspirez-la de nouveau, jusqu'à ce que tous les flacons de votre dose soient dans la seringue ; pour une dose d'un demi-flacon, ne prélevez que la moitié de la solution. Vérifiez que la solution est claire et incolore.",
      },
    },
    {
      id: "fostimon.vial.change-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Change the needle - Put the protective cap back on the preparation needle, remove it and replace it with the fine needle for injection under the skin. Push the fine needle firmly onto the syringe, then twist it slightly to make sure it is fully screwed on. Remove its cap, hold the syringe with the needle pointing up and tap the side gently so any air bubbles rise, then push the plunger until a bead of liquid appears at the tip of the needle. Do not use the solution if it is cloudy or contains particles.",
        fr: "Changer l'aiguille - Remettez le capuchon protecteur sur l'aiguille de préparation, retirez-la et remplacez-la par l'aiguille fine pour l'injection sous-cutanée. Poussez fermement l'aiguille fine sur la seringue, puis tournez-la légèrement pour vous assurer qu'elle est bien vissée. Retirez son capuchon, tenez la seringue aiguille vers le haut et tapotez légèrement le côté pour faire remonter les bulles, puis appuyez doucement sur le piston jusqu'à ce qu'un peu de liquide apparaisse en haut de l'aiguille. N'utilisez pas la solution si elle est trouble ou contient des particules.",
      },
    },
    {
      id: "fostimon.vial.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      // "45° or 90°" is the leaflet's; the figure keeps its right-angle mark.
      id: "fostimon.vial.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Firmly pinch the skin together and, with the other hand, insert the needle with a dart-like motion at an angle of 45° or 90°. Inject under the skin as you were shown; do not inject directly into a vein. Push the plunger slowly and steadily so the solution is injected correctly and the tissues are not damaged, taking as much time as you need to inject the volume prescribed.",
        fr: "Injecter - Pincez fermement la peau entre vos doigts et, de l'autre main, enfoncez l'aiguille d'un coup sec avec un angle de 45° ou 90°. Injectez sous la peau comme on vous l'a indiqué ; n'injectez pas directement dans une veine. Appuyez doucement et fermement sur le piston afin d'injecter correctement la solution et d'éviter d'abîmer les tissus, en prenant tout le temps nécessaire pour injecter le volume prescrit.",
      },
      warnings: [OHSS],
    },
    {
      // The massage is the leaflet's own — the one subcutaneous fertility
      // injection in the catalogue that says to massage rather than not to.
      id: "fostimon.vial.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Pull the needle out quickly and press on the injection site with an alcohol swab; a gentle massage while keeping the pressure helps disperse the solution and relieve any discomfort. Put all the needles and syringes in your sharps container straight away. Do not stop {brand} on your own initiative: always consult your doctor if you are considering stopping.",
        fr: "Après l'injection - Retirez immédiatement l'aiguille et appuyez avec un coton imbibé d'alcool au point d'injection ; un massage doux en maintenant la pression aide à disperser la solution et à soulager tout inconfort. Jetez tout de suite toutes les aiguilles et seringues dans votre collecteur. N'arrêtez pas {brand} de votre propre initiative : si vous souhaitez arrêter le traitement, consultez toujours votre médecin.",
      },
    },
  ],
};
